<script setup>
import appAxios from '@/libs/app-axios'
import axios from 'axios'
import { defineProps, defineEmits, reactive, ref, computed, watchEffect, onBeforeMount } from 'vue'

const props = defineProps(['editId', 'wide'])
const emits = defineEmits(['on-success', 'on-cancel'])

const UNIT_TYPES = ['ELEVATOR', 'ESCALATOR']
const LIFT_STATUSES = ['OPERATIONAL', 'NOT_OPERATIONAL', 'DECOMMISSIONED']
const PM_STATUSES = ['COMPLETE']

/** @type {FormData} */
const blankForm = () => ({
  customer_id: null,
  pm_month: '',
  attention_name: '',
  subject: '',
  pm_activities: '',
  notes: '',
  prepared_by_name: '',
  prepared_by_title: '',
  approved_by_name: '',
  approved_by_title: '',
  units: [],
})

/** @type {FormState} */
const formState = reactive({
  isLoading: false,
  data: blankForm(),
  errors: {},
})

/** @type {{ items: {id: string, company: string}[], isLoading: boolean }} */
const customerState = reactive({
  items: [],
  isLoading: false,
})

const resetForm = () => {
  formState.errors = {}
  const fresh = blankForm()
  Object.keys(fresh).forEach(key => (formState.data[key] = fresh[key]))
}

const getCustomers = async () => {
  try {
    customerState.isLoading = true
    const { data } = await appAxios().get('/v3/customers/dropdown-options')
    customerState.items = data.data || []
  } catch (e) {
    setTimeout(() => getCustomers(), 3000)
  } finally {
    customerState.isLoading = false
  }
}

const loadForEdit = async controller => {
  try {
    const { data: { data } } = await appAxios().get(`/v3/pm-summaries/${props.editId}/edit`, {
      signal: controller?.signal,
    })
    const fresh = blankForm()
    Object.keys(fresh).forEach(key => {
      if (key === 'units') {
        formState.data.units = (data.units || []).map(u => ({ ...u }))
      } else if (key === 'pm_month') {
        formState.data.pm_month = data.pm_month ? data.pm_month.slice(0, 7) : ''
      } else {
        formState.data[key] = data[key] ?? fresh[key]
      }
    })
    formState.errors = {}
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
    setTimeout(() => loadForEdit(controller), 3000)
  }
}

/** @type {{ isLoading: boolean, items: string[] }} */
const clientList = reactive({ isLoading: false, items: [] })

const getClientList = async () => {
  try {
    clientList.isLoading = true
    const { data } = await appAxios().get('/preventive-maintenance-lists/client-list')
    clientList.items = data || []
  } catch (e) {
    setTimeout(() => getClientList(), 3000)
  } finally {
    clientList.isLoading = false
  }
}

const generateClients = ref([])

const canGenerateUnits = computed(() => generateClients.value.length > 0 && !!formState.data.pm_month)

const suggestState = reactive({ isLoading: false })

const generateUnits = async () => {
  if (!canGenerateUnits.value) return
  if (
    formState.data.units.length > 0 &&
    !confirm('This will replace the current units list. Continue?')
  )
    return

  suggestState.isLoading = true
  try {
    const pmMonth = `${formState.data.pm_month}-01`
    const results = await Promise.all(
      generateClients.value.map(client =>
        appAxios().get('/v3/pm-summaries/suggest-units', {
          params: { client, pm_month: pmMonth },
        })
      )
    )
    const seen = new Set()
    const suggested = results.flatMap(r => r.data.data || []).filter(u => {
      if (seen.has(u.unit_code)) return false
      seen.add(u.unit_code)
      return true
    }).sort((a, b) => {
      const typeOrder = (a.unit_type || '').localeCompare(b.unit_type || '')
      if (typeOrder !== 0) return typeOrder
      return (a.unit_code || '').localeCompare(b.unit_code || '', undefined, { numeric: true })
    })
    formState.data.units = suggested.map(u => ({
      id: null,
      unit_code: u.unit_code || '',
      unit_type: u.unit_type || 'ELEVATOR',
      lift_status: u.lift_status || 'OPERATIONAL',
      pm_status: u.pm_status || null,
      remarks: u.remarks || '',
      recommendations: u.recommendations || '',
    }))
  } catch (e) {
    alert(e?.message || 'Failed to generate units')
  } finally {
    suggestState.isLoading = false
  }
}

const addUnit = () => {
  formState.data.units.push({
    id: null,
    unit_code: '',
    unit_type: 'ELEVATOR',
    lift_status: 'OPERATIONAL',
    pm_status: null,
    remarks: '',
    recommendations: '',
  })
}

const removeUnit = index => {
  formState.data.units.splice(index, 1)
}

const onSubmitHandler = async e => {
  e.preventDefault()
  formState.errors = {}
  formState.isLoading = true

  const payload = {
    ...formState.data,
    pm_month: formState.data.pm_month ? `${formState.data.pm_month}-01` : '',
    units: formState.data.units.map(u => ({
      id: u.id || undefined,
      unit_code: u.unit_code,
      unit_type: u.unit_type,
      lift_status: u.lift_status,
      pm_status: u.pm_status || null,
      remarks: u.remarks || null,
      recommendations: u.recommendations || null,
    })),
  }

  try {
    if (props.editId) {
      await appAxios().put(`/v3/pm-summaries/${props.editId}`, payload)
    } else {
      await appAxios().post('/v3/pm-summaries', payload)
    }
    emits('on-success')
    if (!props.editId) resetForm()
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.data?.errors) {
      const { errors } = e.response.data
      Object.keys(errors).forEach(key => (formState.errors[key] = errors[key]))
    } else {
      alert(e?.message || 'An error occurred')
    }
  } finally {
    formState.isLoading = false
  }
}

const onCancelHandler = () => {
  resetForm()
  emits('on-cancel')
}

watchEffect(onCleanup => {
  if (!props.editId) return
  const controller = new AbortController()
  loadForEdit(controller)
  onCleanup(() => controller.abort())
})

onBeforeMount(() => {
  getCustomers()
  getClientList()
})
</script>

<style lang="sass" scoped>
.pm-summary-form
  display: flex
  flex-direction: column
  gap: 12px

  .section-title
    font-weight: bold
    font-size: 13px
    color: #555
    text-transform: uppercase
    letter-spacing: 0.5px

  .two-col
    display: grid
    grid-template-columns: 1fr 1fr
    gap: 12px

  .actions
    display: flex
    gap: 10px
    > *
      flex: 1

  .units-header
    display: flex
    align-items: center
    justify-content: space-between

  .generate-units-row
    display: flex
    gap: 8px
    align-items: center
    > .v-input
      flex: 1

  .unit-row
    border: 1px solid #e0e0e0
    border-radius: 8px
    padding: 12px
    display: flex
    flex-direction: column
    gap: 8px
    .unit-row-header
      display: flex
      justify-content: space-between
      align-items: center
      font-size: 12px
      color: #777

  .fields-col,
  .units-col
    display: flex
    flex-direction: column
    gap: 12px

// Wide (page) layout
.pm-summary-form.pm-summary-form--wide
  flex-direction: row
  align-items: flex-start

  .fields-col
    width: 380px
    flex-shrink: 0

  .units-col
    flex: 1
    min-width: 0

  .units-table-wrap
    overflow-x: auto
    border: 1px solid #e0e0e0
    border-radius: 8px

  .units-table
    width: 100%
    border-collapse: collapse

    thead tr
      background: #f5f5f5

    th
      padding: 8px 8px
      text-align: left
      font-size: 11px
      font-weight: 600
      color: #555
      text-transform: uppercase
      letter-spacing: 0.4px
      white-space: nowrap
      border-bottom: 2px solid #e0e0e0

    td
      padding: 4px 6px
      vertical-align: top
      border-bottom: 1px solid #f0f0f0

    tbody tr:last-child td
      border-bottom: none

    .td-num
      width: 36px
      text-align: center
      padding-top: 14px
      font-size: 12px
      color: #999

    .td-code
      min-width: 110px

    .td-type
      min-width: 130px

    .td-lift
      min-width: 195px

    .td-pm
      min-width: 120px

    .td-text
      min-width: 140px

    .td-action
      width: 44px
      text-align: center
      padding-top: 8px

  .units-empty
    color: #999
    font-size: 13px
    text-align: center
    padding: 20px 0
</style>

<template>
  <form @submit.prevent="onSubmitHandler">
    <div class="pm-summary-form" :class="{ 'pm-summary-form--wide': wide }">

      <!-- ── Fields column ── -->
      <div class="fields-col">
        <div class="section-title">General Info</div>

        <v-autocomplete
          outlined
          dense
          hide-details="auto"
          label="Customer *"
          v-model="formState.data.customer_id"
          :items="customerState.items"
          item-value="id"
          item-text="company"
          :loading="customerState.isLoading"
          :error-messages="formState.errors.customer_id"
        />

        <v-text-field
          outlined
          dense
          hide-details="auto"
          label="PM Month *"
          type="month"
          v-model="formState.data.pm_month"
          :error-messages="formState.errors.pm_month"
        />

        <v-text-field
          outlined
          dense
          hide-details="auto"
          label="Attention Name"
          v-model="formState.data.attention_name"
          :error-messages="formState.errors.attention_name"
        />

        <v-text-field
          outlined
          dense
          hide-details="auto"
          label="Subject"
          v-model="formState.data.subject"
          :error-messages="formState.errors.subject"
        />

        <v-textarea
          outlined
          dense
          hide-details="auto"
          label="PM Activities"
          v-model="formState.data.pm_activities"
          :error-messages="formState.errors.pm_activities"
          :rows="3"
        />

        <v-textarea
          outlined
          dense
          hide-details="auto"
          label="Notes"
          v-model="formState.data.notes"
          :error-messages="formState.errors.notes"
          :rows="3"
        />

        <div class="section-title">Signatories</div>

        <div class="two-col">
          <v-text-field
            outlined
            dense
            hide-details="auto"
            label="Prepared By (Name)"
            v-model="formState.data.prepared_by_name"
            :error-messages="formState.errors.prepared_by_name"
          />
          <v-text-field
            outlined
            dense
            hide-details="auto"
            label="Prepared By (Title)"
            v-model="formState.data.prepared_by_title"
            :error-messages="formState.errors.prepared_by_title"
          />
          <v-text-field
            outlined
            dense
            hide-details="auto"
            label="Approved By (Name)"
            v-model="formState.data.approved_by_name"
            :error-messages="formState.errors.approved_by_name"
          />
          <v-text-field
            outlined
            dense
            hide-details="auto"
            label="Approved By (Title)"
            v-model="formState.data.approved_by_title"
            :error-messages="formState.errors.approved_by_title"
          />
        </div>

        <!-- Actions stay in fields column on wide layout -->
        <div v-if="props.editId" class="actions">
          <v-btn color="warning" type="submit" :loading="formState.isLoading" :disabled="formState.isLoading">
            Update
          </v-btn>
          <v-btn color="error" type="button" @click="onCancelHandler">
            Cancel
          </v-btn>
        </div>
        <v-btn v-else color="primary" dark type="submit" :loading="formState.isLoading" :disabled="formState.isLoading">
          Submit
        </v-btn>
      </div>

      <!-- ── Units column ── -->
      <div class="units-col">
        <div class="units-header">
          <span class="section-title">Units</span>
          <v-btn small outlined color="primary" type="button" @click="addUnit">
            <v-icon small left>mdi-plus</v-icon>
            Add Unit
          </v-btn>
        </div>

        <div class="generate-units-row">
          <v-combobox
            dense
            outlined
            hide-details
            label="Client(s) (for generating units)"
            v-model="generateClients"
            :items="clientList.items"
            :loading="clientList.isLoading"
            multiple
            chips
            small-chips
            deletable-chips
            clearable
          />
          <v-btn
            outlined
            color="teal"
            type="button"
            :disabled="!canGenerateUnits"
            :loading="suggestState.isLoading"
            @click="generateUnits"
          >
            <v-icon small left>mdi-auto-fix</v-icon>
            Generate
          </v-btn>
        </div>

        <!-- Wide: table layout -->
        <template v-if="wide">
          <div class="units-table-wrap">
            <table class="units-table">
              <thead>
                <tr>
                  <th class="td-num">#</th>
                  <th class="td-code">Unit Code</th>
                  <th class="td-type">Type</th>
                  <th class="td-lift">Lift Status</th>
                  <th class="td-pm">PM Status</th>
                  <th class="td-text">Remarks</th>
                  <th class="td-text">Recommendations</th>
                  <th class="td-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(unit, index) in formState.data.units" :key="index">
                  <td class="td-num">{{ index + 1 }}</td>
                  <td class="td-code">
                    <v-text-field
                      outlined dense hide-details="auto"
                      v-model="unit.unit_code"
                      :error-messages="formState.errors[`units.${index}.unit_code`]"
                    />
                  </td>
                  <td class="td-type">
                    <v-select
                      outlined dense hide-details="auto"
                      v-model="unit.unit_type"
                      :items="UNIT_TYPES"
                      :error-messages="formState.errors[`units.${index}.unit_type`]"
                    />
                  </td>
                  <td class="td-lift">
                    <v-select
                      outlined dense hide-details="auto"
                      v-model="unit.lift_status"
                      :items="LIFT_STATUSES"
                      :error-messages="formState.errors[`units.${index}.lift_status`]"
                    />
                  </td>
                  <td class="td-pm">
                    <v-select
                      outlined dense hide-details="auto"
                      v-model="unit.pm_status"
                      :items="PM_STATUSES"
                      clearable
                      :error-messages="formState.errors[`units.${index}.pm_status`]"
                    />
                  </td>
                  <td class="td-text">
                    <v-text-field
                      outlined dense hide-details="auto"
                      v-model="unit.remarks"
                      :error-messages="formState.errors[`units.${index}.remarks`]"
                    />
                  </td>
                  <td class="td-text">
                    <v-text-field
                      outlined dense hide-details="auto"
                      v-model="unit.recommendations"
                      :error-messages="formState.errors[`units.${index}.recommendations`]"
                    />
                  </td>
                  <td class="td-action">
                    <v-btn icon small color="red" type="button" @click="removeUnit(index)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="formState.data.units.length === 0" class="units-empty">
              No units yet. Click "Add Unit" or use Generate.
            </div>
          </div>
        </template>

        <!-- Narrow: card layout -->
        <template v-else>
          <div v-for="(unit, index) in formState.data.units" :key="index" class="unit-row">
            <div class="unit-row-header">
              <span>Unit {{ index + 1 }}</span>
              <v-btn icon small color="red" type="button" @click="removeUnit(index)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>

            <div class="two-col">
              <v-text-field
                outlined dense hide-details="auto"
                label="Unit Code *"
                v-model="unit.unit_code"
                :error-messages="formState.errors[`units.${index}.unit_code`]"
              />
              <v-select
                outlined dense hide-details="auto"
                label="Unit Type *"
                v-model="unit.unit_type"
                :items="UNIT_TYPES"
                :error-messages="formState.errors[`units.${index}.unit_type`]"
              />
              <v-select
                outlined dense hide-details="auto"
                label="Lift Status *"
                v-model="unit.lift_status"
                :items="LIFT_STATUSES"
                :error-messages="formState.errors[`units.${index}.lift_status`]"
              />
              <v-select
                outlined dense hide-details="auto"
                label="PM Status"
                v-model="unit.pm_status"
                :items="PM_STATUSES"
                clearable
                :error-messages="formState.errors[`units.${index}.pm_status`]"
              />
            </div>

            <v-text-field
              outlined dense hide-details="auto"
              label="Remarks"
              v-model="unit.remarks"
              :error-messages="formState.errors[`units.${index}.remarks`]"
            />
            <v-text-field
              outlined dense hide-details="auto"
              label="Recommendations"
              v-model="unit.recommendations"
              :error-messages="formState.errors[`units.${index}.recommendations`]"
            />
          </div>

          <div v-if="formState.data.units.length === 0" style="color: #999; font-size: 13px; text-align: center; padding: 8px 0;">
            No units added yet. Click "Add Unit" to add one.
          </div>
        </template>
      </div>

    </div>
  </form>
</template>

<script>
export default {
  name: 'PmSummaryForm',
}
/**
 * @typedef {Object} FormData
 * @property {string|null} customer_id
 * @property {string} pm_month
 * @property {string} attention_name
 * @property {string} subject
 * @property {string} pm_activities
 * @property {string} notes
 * @property {string} prepared_by_name
 * @property {string} prepared_by_title
 * @property {string} approved_by_name
 * @property {string} approved_by_title
 * @property {UnitRow[]} units
 *
 * @typedef {Object} UnitRow
 * @property {number|null} id
 * @property {string} unit_code
 * @property {'ELEVATOR'|'ESCALATOR'} unit_type
 * @property {'OPERATIONAL'|'NOT_OPERATIONAL'|'DECOMMISSIONED'} lift_status
 * @property {'COMPLETE'|null} pm_status
 * @property {string|null} remarks
 * @property {string|null} recommendations
 *
 * @typedef {Object} FormState
 * @property {boolean} isLoading
 * @property {FormData} data
 * @property {Object} errors
 */
</script>
